import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Stack,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import { Download, CalendarToday } from "@mui/icons-material";
import * as XLSX from "xlsx";

const UserResponses = () => {
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Calculate date range based on filter
  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "today":
        return {
          startDate: today.toISOString(),
          endDate: now.toISOString(),
        };

      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          startDate: yesterday.toISOString(),
          endDate: today.toISOString(),
        };
      }

      case "last7days": {
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        return {
          startDate: last7.toISOString(),
          endDate: now.toISOString(),
        };
      }

      case "last30days": {
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        return {
          startDate: last30.toISOString(),
          endDate: now.toISOString(),
        };
      }

      case "custom":
        if (customStartDate && customEndDate) {
          return {
            startDate: new Date(customStartDate).toISOString(),
            endDate: new Date(`${customEndDate}T23:59:59`).toISOString(),
          };
        }
        return null;

      default:
        return null;
    }
  };

  // ✅ FIX: Memoized API endpoint
  const apiEndpoint = useMemo(() => {
    const dateRange = getDateRange();
    if (!dateRange) return "/rcs/user-responses";

    return `/rcs/user-responses?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
  }, [dateFilter, customStartDate, customEndDate]);

  const { data, isLoading, isError, error } = useApiEp(apiEndpoint);

  // Export to Excel
  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        "Message ID": item.messageId,
        From: item.from,
        Message: item.body,
        "Reply Type": item.replyType,
        "Date & Time": new Date(item.createdOn).toLocaleString(),
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Responses");
    XLSX.writeFile(
      workbook,
      `RCS_User_Responses_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const getReplyTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "positive":
        return "success";
      case "negative":
        return "error";
      case "neutral":
        return "default";
      default:
        return "primary";
    }
  };

  const handleDateFilterChange = (_, newFilter) => {
    if (newFilter !== null) setDateFilter(newFilter);
  };

  if (isLoading) {
    return (
      <Container
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ mt: 3 }}>
        <BackBtn />
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading user responses: {error?.message || "Unknown error"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 3, mb: 4 }}>
      <BackBtn />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            RCS Campaign Responses
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage all user responses from your RCS marketing campaign.
          </Typography>
        </Box>

        {data?.length > 0 && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={exportToExcel}
          >
            Export Excel
          </Button>
        )}
      </Box>

      {/* Date Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          <CalendarToday sx={{ fontSize: 16, mr: 0.5 }} />
          Filter by Date
        </Typography>

        <Stack spacing={2}>
          <ToggleButtonGroup
            value={dateFilter}
            exclusive
            onChange={handleDateFilterChange}
            size="small"
          >
            <ToggleButton value="all">All Time</ToggleButton>
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="yesterday">Yesterday</ToggleButton>
            <ToggleButton value="last7days">Last 7 Days</ToggleButton>
            <ToggleButton value="last30days">Last 30 Days</ToggleButton>
            <ToggleButton value="custom">Custom Range</ToggleButton>
          </ToggleButtonGroup>

          {dateFilter === "custom" && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Start Date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="End Date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{ min: customStartDate }}
              />
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {!data || data.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">No responses found</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ backgroundColor: "primary.main" }}>
              <TableRow>
                {[
                  "Message ID",
                  "From",
                  "Message",
                  "Reply Type",
                  "Date & Time",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r, i) => (
                <TableRow key={r.messageId || i}>
                  <TableCell>{r.messageId}</TableCell>
                  <TableCell>{r.from}</TableCell>
                  <TableCell sx={{ maxWidth: 300, wordBreak: "break-word" }}>
                    {r.body}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.replyType || "Text reply"}
                      color={getReplyTypeColor(r.replyType)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(r.createdOn).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Container>
  );
};

export default UserResponses;
